import { useState, useEffect, useMemo } from "react";
import { useBrandsApi } from "@/hooks/useBrandsApi";
import { useUsersApi } from "@/hooks/useUsersApi";
import { useAuth } from "@/hooks/useAuth";

export function useGetAvailableBrands() {
  const { brands, fetchAllBrands } = useBrandsApi();
  const { getUserByEmail } = useUsersApi();
  const { user, isManager } = useAuth();

  const [isAdmin, setIsAdmin] = useState(false);
  const [userBrands, setUserBrands] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  useEffect(() => {
    const checkUserType = async () => {
      if (user?.email) {
        try {
          const userData = await getUserByEmail(user.email);

          const isUserAdmin = Boolean(userData?.userType === "admin");
          setIsAdmin(isUserAdmin);

          // Si el usuario no es admin, obtener sus marcas asignadas
          if (!isUserAdmin && userData?.roleSpecificData?.brands) {
            setUserBrands(userData.roleSpecificData.brands);
          } else if (isUserAdmin) {
            setUserBrands([]);
          }
        } catch (error) {
          setIsAdmin(false);
          setUserBrands([]);
        } finally {
          setIsLoadingUser(false);
        }
      } else {
        setIsLoadingUser(false);
      }
    };

    checkUserType();
    fetchAllBrands(isManager ? user : null);
  }, [user?.email, getUserByEmail, fetchAllBrands]);

  // Calcular las marcas disponibles según los permisos del usuario
  const availableBrands = useMemo(() => {
    if (isAdmin) {
      return brands;
    } else {
      // Filtrar las marcas globales para mostrar solo las que el usuario tiene asignadas
      const filteredBrands = brands.filter((brand) =>
        userBrands.some((userBrand) => userBrand.id === brand.id),
      );
      return filteredBrands;
    }
  }, [isAdmin, brands, userBrands]);

  return {
    availableBrands,
    isAdmin,
    userBrands,
    isLoadingUser,
  };
}
